import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Mail,
    Phone,
    Building2,
    Briefcase,
    ShieldCheck,
    User as UserIcon,
    CheckCircle2,
} from "lucide-react";
import { superAdminApi, type User } from "../modules/superAdmin/api";

const formatRole = (role?: string) =>
    (role || "")
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());

const Profile = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState<User | null>(null);

    useEffect(() => {
        superAdminApi
            .getMyProfile()
            .then(setUser)
            .catch((err) => console.error("Failed to load profile:", err));
    }, []);

    const fullName = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim();
    const initials = fullName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase();

    const isActive = (user?.status || "").toLowerCase() === "active";

    const infoItems = [
        { label: "First Name", value: user?.first_name, icon: UserIcon },
        { label: "Last Name", value: user?.last_name, icon: UserIcon },
        { label: "Email", value: user?.email, icon: Mail },
        { label: "Phone Number", value: user?.phone_number, icon: Phone },
        { label: "Department", value: user?.department, icon: Briefcase },
        { label: "Company", value: user?.tenant_name, icon: Building2 },
    ];

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            {/* Page Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">
                        My Profile
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Manage your account information
                    </p>
                </div>
                <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50 transition"
                >
                    <ArrowLeft size={16} />
                    Back
                </button>
            </div>

            {/* Profile Hero Card */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">

                {/* Gradient Banner */}
                <div className="h-32 sm:h-36 relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">

                    {/* Soft glow */}
                    <div className="absolute -top-20 -left-16 w-72 h-72 rounded-full bg-white/10 blur-2xl" />

                    <div className="absolute -top-32 right-0 w-96 h-96 rounded-full bg-fuchsia-400/20 blur-3xl" />

                    {/* Decorative curved shape */}
                    <div className="absolute -bottom-24 left-[25%] w-[55%] h-44 rounded-[50%] border border-white/10 bg-white/[0.04]" />

                    <div className="absolute -bottom-28 left-[35%] w-[45%] h-44 rounded-[50%] border border-white/10" />

                    {/* Decorative dots - left */}
                    <div className="absolute top-7 left-1/4 grid grid-cols-5 gap-2 opacity-25">
                        {Array.from({ length: 15 }).map((_, index) => (
                            <span
                                key={index}
                                className="w-1.5 h-1.5 rounded-full bg-white"
                            />
                        ))}
                    </div>

                    {/* Decorative dots - right */}
                    <div className="absolute top-6 right-1/4 grid grid-cols-5 gap-2 opacity-20">
                        {Array.from({ length: 15 }).map((_, index) => (
                            <span
                                key={index}
                                className="w-1.5 h-1.5 rounded-full bg-white"
                            />
                        ))}
                    </div>

                    {/* Subtle radial highlight */}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(255,255,255,0.18),transparent_35%)]" />
                </div>

                {/* Profile Info */}
                <div className="relative px-5 sm:px-8 pb-6">

                    <div className="flex items-center justify-between gap-5">

                        {/* Left: Avatar + Name */}
                        <div className="flex items-center gap-4 sm:gap-5 min-w-0">

                            {/* Avatar */}
                            <div
                                className="
                        -mt-12 sm:-mt-14
                        relative
                        z-10
                        w-20 h-20
                        sm:w-24 sm:h-24
                        shrink-0
                        rounded-2xl
                        bg-white
                        border-4 border-white
                        shadow-xl
                        flex
                        items-center
                        justify-center
                        overflow-hidden
                    "
                            >
                                {/* Avatar inner gradient */}
                                <div className="
                        absolute inset-0
                        bg-gradient-to-br
                        from-blue-50
                        via-white
                        to-indigo-50
                    " />

                                <span className="
                        relative
                        text-2xl
                        sm:text-3xl
                        font-extrabold
                        bg-gradient-to-br
                        from-blue-600
                        via-indigo-600
                        to-purple-600
                        bg-clip-text
                        text-transparent
                    ">
                                    {initials || "?"}
                                </span>
                            </div>

                            {/* Name + Role */}
                            <div className="min-w-0 pt-1">

                                <h2 className="
                        text-xl
                        sm:text-2xl
                        font-bold
                        text-slate-900
                        truncate
                    ">
                                    {fullName || "—"}
                                </h2>

                                <div className="flex items-center gap-2 mt-1">

                                    <span className="
                            text-xs
                            sm:text-sm
                            font-medium
                            text-slate-500
                            uppercase
                            tracking-wide
                        ">
                                        {formatRole(user?.role) || "—"}
                                    </span>

                                </div>
                            </div>
                        </div>

                        {/* Right: Status */}
                        <span
                            className={`
                    shrink-0
                    inline-flex
                    items-center
                    gap-2
                    px-3
                    sm:px-4
                    py-1.5
                    rounded-full
                    text-xs
                    font-semibold
                    uppercase
                    tracking-wide
                    shadow-sm
                    border
                    ${isActive
                                    ? `
                                bg-emerald-50
                                text-emerald-700
                                border-emerald-200
                            `
                                    : `
                                bg-rose-50
                                text-rose-700
                                border-rose-200
                            `
                                }
                `}
                        >
                            <span
                                className={`
                        w-2
                        h-2
                        rounded-full
                        ${isActive
                                        ? "bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.12)]"
                                        : "bg-rose-500 shadow-[0_0_0_3px_rgba(244,63,94,0.12)]"
                                    }
                    `}
                            />

                            {user?.status || "Unknown"}
                        </span>
                    </div>
                </div>
            </div>

            {/* Personal Information */}
            <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-blue-100 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm shadow-sm">
                        <ShieldCheck size={16} />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                            Personal Information
                        </h3>
                        <p className="text-xs text-blue-700/70">
                            Your account details and role information
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-100">
                    {infoItems.map(({ label, value, icon: Icon }) => (
                        <div
                            key={label}
                            className="bg-white p-5 hover:bg-blue-50/40 transition-colors"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <Icon size={14} className="text-gray-400" />
                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    {label}
                                </span>
                            </div>
                            <p className="text-sm font-medium text-slate-800 break-words">
                                {value || "—"}
                            </p>
                        </div>
                    ))}

                    {/* Role cell (formatted) */}
                    <div className="bg-white p-5 hover:bg-blue-50/40 transition-colors">
                        <div className="flex items-center gap-2 mb-2">
                            <ShieldCheck size={14} className="text-gray-400" />
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Role
                            </span>
                        </div>
                        <p className="text-sm font-medium text-slate-800">
                            {formatRole(user?.role) || "—"}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;